import {announce} from '@github-ui/aria-live'
import {CrossReferenceIcon, CopyIcon, CheckIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, Heading, Link as PrimerLink, UnderlineNav} from '@primer/react'
import {SkeletonText} from '@primer/react/experimental'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useState, Suspense} from 'react'
import {queryOptions, useQueryClient, useSuspenseQuery} from '@github-ui/react-query'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {CitationData} from '../types'
import styles from './SidebarCitation.module.css'

function CopyableField({value, label}: {value: string; label: string}) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(value)
    setCopied(true)
    announce(`${label} copied to clipboard`)
    setTimeout(() => setCopied(false), 2000)
  }, [label, value])

  return (
    <div className={styles.copyField}>
      <input
        type="text"
        className={styles.copyInput}
        value={value}
        aria-label={label}
        readOnly
        onFocus={e => e.target.select()}
      />
      <button type="button" aria-label={`Copy ${label}`} onClick={handleCopy} className={styles.copyButton}>
        {copied ? <CheckIcon size={16} /> : <CopyIcon size={16} />}
      </button>
    </div>
  )
}

function CitationContent({owner, repo}: {owner: string; repo: string}) {
  const {data: citation} = useSuspenseQuery(citationQuery(owner, repo))
  const [selectedTab, setSelectedTab] = useState<'apa' | 'bibtex'>('apa')

  if (!citation) {
    return <p className={styles.overlayDescription}>Something went wrong loading citation data.</p>
  }

  const hasApa = !!citation.apa
  const hasBibtex = !!citation.bibtex

  return (
    <>
      <Heading as="h3" className={styles.overlayTitle}>
        Cite this repository
      </Heading>
      <p className={styles.overlayDescription}>
        If you use this software in your work, please cite it using the following metadata.{' '}
        <PrimerLink href={citation.helpUrl} inline>
          Learn more about CITATION files.
        </PrimerLink>
      </p>

      <UnderlineNav aria-label="Citations">
        {hasApa && (
          <UnderlineNav.Item
            aria-current={selectedTab === 'apa' ? 'page' : undefined}
            onSelect={() => setSelectedTab('apa')}
          >
            APA
          </UnderlineNav.Item>
        )}
        {hasBibtex && (
          <UnderlineNav.Item
            aria-current={selectedTab === 'bibtex' ? 'page' : undefined}
            onSelect={() => setSelectedTab('bibtex')}
          >
            BibTeX
          </UnderlineNav.Item>
        )}
      </UnderlineNav>

      <div className={styles.tabPanel}>
        {selectedTab === 'apa' && citation.apa && <CopyableField value={citation.apa} label="APA citation" />}
        {selectedTab === 'bibtex' && citation.bibtex && (
          <CopyableField value={citation.bibtex} label="BibTeX citation" />
        )}
      </div>

      {citation.filePath && (
        <div className={styles.footer}>
          <Button as="a" href={citation.filePath} block>
            View citation file
          </Button>
        </div>
      )}
    </>
  )
}

const citationQuery = (ownerLogin: string, repoName: string) =>
  queryOptions({
    queryKey: ['code-view-sidebar', 'citation', {ownerLogin, repoName}],
    queryFn: async () => {
      const response = await verifiedFetchJSON(`/${ownerLogin}/${repoName}/_sidebar/citation`)
      if (!response.ok) throw new Error(`Citation fetch failed: ${response.status}`)
      return response.json() as Promise<CitationData | null>
    },
    staleTime: Infinity,
  })

export function SidebarCitation({ownerLogin, repoName}: {ownerLogin: string; repoName: string}) {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()

  const handleOpen = useCallback(() => {
    queryClient.prefetchQuery(citationQuery(ownerLogin, repoName))
    setOpen(true)
  }, [queryClient, ownerLogin, repoName])

  return (
    <AnchoredOverlay
      open={open}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      width="medium"
      side="outside-bottom"
      align="end"
      overlayProps={{
        role: 'dialog',
        'aria-label': 'Cite this repository',
        style: {minWidth: '344px'},
      }}
      renderAnchor={props => (
        <button {...props} type="button" className={styles.triggerButton}>
          <CrossReferenceIcon className="mr-2" />
          <span>Cite this repository</span>
          <span className={styles.caret} />
        </button>
      )}
    >
      <div className={styles.overlayContent}>
        <ErrorBoundary
          fallback={<p className={styles.overlayDescription}>Something went wrong loading citation data.</p>}
        >
          <Suspense fallback={<SkeletonText lines={4} />}>
            {open ? <CitationContent owner={ownerLogin} repo={repoName} /> : null}
          </Suspense>
        </ErrorBoundary>
      </div>
    </AnchoredOverlay>
  )
}
